import React from "react";
import { Box } from "@mui/material";
import ApplicationCheckBox from "my-tax/components/application-check-box";
import useStyles from "my-tax/components/family-tax-credit-application-print/family-tax-credit-application-print.style";
import MyTaxCreditApplicationPrintEmployer from "my-tax/components/tax-credit-application-print-employer/tax-credit-application-print-employer";
import { DOTTED_LINE } from "my-tax/constants";

interface UnderThirtyMothersTaxCreditApplicationPrintProps {
  fullName: string;
  taxNumber: string;
  isSuitable: boolean;
  isSuitableForChild: boolean;
  isSuitableForFetus: boolean;
  isSuitableForAdopted: boolean;
  allDiscountsThirtyMothers: boolean;
  partDiscountsThirtyMothers: boolean;
  underThirtyMothersAmountDiscount: number;
  isNoneApplyStartMonthThirtyMothers: string;
  employerName: string;
  employerTaxNumber: string;
  isModifiedApplication: boolean;
  applicationDate: string;
  childName: string;
  childTaxNumber: string;
  fetusYear: string;
  fetusMonth: string;
  adoptedChildName: string;
  adoptedChildTaxNumber: string;
  isNewVersion: boolean;
}

export const MyTaxUnderThirtyMothersTaxCreditApplicationPrint =
  React.forwardRef(
    (props: UnderThirtyMothersTaxCreditApplicationPrintProps, ref) => {
      const {
        fullName,
        taxNumber,
        isSuitable,
        isSuitableForChild,
        isSuitableForFetus,
        isSuitableForAdopted,
        allDiscountsThirtyMothers,
        partDiscountsThirtyMothers,
        underThirtyMothersAmountDiscount,
        isNoneApplyStartMonthThirtyMothers,
        employerName,
        employerTaxNumber,
        isModifiedApplication,
        applicationDate,
        childName,
        childTaxNumber,
        fetusYear,
        fetusMonth,
        adoptedChildName,
        adoptedChildTaxNumber,
        isNewVersion
      } = props;
      let year = applicationDate?.slice(0, 4);

      const classes = useStyles();

      return (
        <Box ref={ref}>
          <style type="text/css" media="print">
            {
              "\
            @page { size: landscape; }\
        "
            }
          </style>

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            fontFamily="EB Garamond"
            fontSize="15px"
            style={{ paddingTop: "20px" }}
          >
            <Box
              style={{
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Adóelőleg-nyilatkozat a 30 év alatti anyák kedvezményének
              érvényesítéséről
              <Box />
              <Box
                key="nyilatkozat-ev"
                display="flex"
                flexDirection="column"
                alignItems="center"
                paddingTop="2px"
              >
                A nyilatkozat benyújtásának éve: {year}
              </Box>
            </Box>
            <Box className={classes.blackBorder}>
              <Box marginLeft="-20px">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Box display="flex">
                      <Box className={classes.startChapter}>I.</Box>
                      <Box fontWeight="normal">
                        A nyilatkozatot adó magánszemély
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-around"
                    style={{
                      marginTop: "5px",
                      marginBottom: "5px",
                    }}
                  >
                    neve:
                    <Box width="200px" textAlign="center">
                      {fullName}
                    </Box>
                    <Box>adóazonosító jele:</Box>
                    <Box marginLeft="-40px">
                      {taxNumber ? taxNumber : DOTTED_LINE}
                    </Box>
                    <Box display="flex" alignItems="center">
                      Módosított nyilatkozat:
                      <ApplicationCheckBox value={isModifiedApplication} />
                    </Box>
                  </Box>
                  <Box height="15px" />
                  <Box display="flex">
                    <Box className={classes.startChapter}>1.</Box>
                    <Box fontWeight="normal" display="flex">
                      {isNewVersion
                        ? "Nyilatkozom, hogy a 30 év alatti anyák kedvezményére a 2. pont szerinti gyermekre, magzatra tekintettel jogosult vagyok"
                        : "Nyilatkozom, hogy a 30 év alatti anyák kedvezményére, a 2. pont szerinti gyermekre, magzatra tekintettel jogosult vagyok"
                      }
                      <ApplicationCheckBox value={isSuitable ? true : false} />
                    </Box>
                  </Box>
                  <Box height="15px" />
                  <Box display="flex">
                    <Box className={classes.startChapter}>2.</Box>
                    <Box>A 30 év alatti anyák kedvezményére</Box>
                  </Box>
                  <Box height="15px" />
                  <Box marginLeft="20px">
                    <Box display="flex">
                      a) <ApplicationCheckBox value={isSuitableForChild} />
                      <Box width="5px" />{" "}
                      {isNewVersion
                        ? `a ${childName?.length !== 0 && isSuitableForChild ? childName : DOTTED_LINE} nevű, ${childTaxNumber?.length !== 0 && isSuitableForChild ? childTaxNumber : DOTTED_LINE} adóazonosító jelű vér szerinti vagy örökbefogadott gyermekre tekintettel vagyok jogosult,`
                        : `a 2022. december 31 után született ${childName?.length !== 0 ? childName : DOTTED_LINE} nevű, ${childTaxNumber?.length !== 0 ? childTaxNumber : DOTTED_LINE} adóazonosító jelű gyermekre tekintettel vagyok jogosult.`
                      }
                    </Box>
                    <Box height="15px" />
                    <Box display="flex">
                      b) <ApplicationCheckBox value={isSuitableForFetus} />
                      <Box width="5px" /> magzat után vagyok jogosult, a
                      várandósság 91. napját{" "}
                      {fetusYear?.length !== 0 && isSuitableForFetus ? fetusYear : DOTTED_LINE} év{" "}
                      {fetusMonth?.length !== 0 && isSuitableForFetus ? fetusMonth : DOTTED_LINE}{" "}
                      hónapban töltöttem be
                    </Box>
                    {!isNewVersion && (
                      <>
                        <Box height="15px" />
                        <Box display="flex">
                          c) <ApplicationCheckBox value={isSuitableForAdopted} />
                          <Box width="5px" /> a 2022. december 31 után
                          örökbefogadott{" "}
                          {adoptedChildName?.length !== 0
                            ? adoptedChildName
                            : DOTTED_LINE}{" "}
                          nevű,{" "}
                          {adoptedChildTaxNumber?.length !== 0
                            ? adoptedChildTaxNumber
                            : DOTTED_LINE}{" "}
                          adóazonosító jelű gyermekre tekintettel vagyok jogosult.
                        </Box>
                      </>
                    )}
                  </Box>
                  {!isNewVersion &&
                    <>
                      <Box height="15px" />
                      <Box display="flex">
                        <Box className={classes.startChapter}>3.</Box>
                        <Box fontWeight="normal" display="flex">
                          Kérem a kedvezményt az adóelőleg megállapítása során:
                          teljes összegben{" "}
                          <ApplicationCheckBox
                            value={allDiscountsThirtyMothers ? true : false}
                          />
                          <Box marginLeft="2px">,</Box>
                          <Box width="5px" />
                          {underThirtyMothersAmountDiscount === 0
                            ? DOTTED_LINE
                            : underThirtyMothersAmountDiscount}{" "}
                          forint összegben
                          <ApplicationCheckBox
                            value={partDiscountsThirtyMothers ? true : false}
                          />
                          <Box width="5px" />
                          figyelembe venni.
                        </Box>
                      </Box>
                    </>
                  }
                  <Box height="15px" />
                  <Box display="flex">
                    {!isNewVersion ? <Box className={classes.startChapter}>4.</Box> :
                      <Box className={classes.startChapter}>3.</Box>
                    }
                    <Box fontWeight="normal" display="flex">
                      Nyilatkozom, hogy a 30 év alatti anyák kedvezményét
                      <Box width="5px" />
                      {isNoneApplyStartMonthThirtyMothers !== "  "
                        ? isNoneApplyStartMonthThirtyMothers
                        : DOTTED_LINE}{" "}
                      <Box width="5px" />
                      hónaptól kezdődően nem (erre a hónapra sem) kívánom
                      igénybe venni.
                    </Box>
                  </Box>
                  <Box height="15px" />
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    marginLeft="20px"
                  >
                    <Box
                      style={{
                        display: "flex",
                      }}
                    >
                      Kelt:
                      <Box width="150px" textAlign="center">
                        {applicationDate}
                      </Box>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      A nyilatkozatot tevő magánszemély aláírása:
                      <Box marginLeft="10px">
                        .............................................................
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            <MyTaxCreditApplicationPrintEmployer
              employerName={employerName}
              employerTaxNumber={employerTaxNumber}
            />
          </Box>
        </Box>
      );
    },
  );

MyTaxUnderThirtyMothersTaxCreditApplicationPrint.displayName =
  "UnderThirtyMothersTaxCreditApplicationPrint";

export default MyTaxUnderThirtyMothersTaxCreditApplicationPrint;
